import React from "react";
import { motion } from "framer-motion";
import { CheckCircle2 } from "lucide-react";

const features = [
  "Verified Professionals",
  "Instant Booking",
  "Secure Payments",
  "Live Tracking",
  "Trusted Ratings",
  "Comprehensive Insurance"
];

const stats = [
  { label: "Professionals", value: "2500+" },
  { label: "Bookings", value: "150K+" },
  { label: "Rating", value: "4.9★" },
  { label: "Success Rate", value: "98%" }
];

export default function HowItWorks() {
  const containerVariants = {
    hidden: { opacity: 0 },
    show: {
      opacity: 1,
      transition: { staggerChildren: 0.1 }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    show: { opacity: 1, y: 0, transition: { duration: 0.5 } }
  };

  return (
    <div className="bg-[#EEF5FF]">
      {/* Why Choose Us Section (Split Layout) */}
      <section className="py-24 lg:py-32 overflow-hidden">
        <div className="container mx-auto px-4 md:px-6">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            
            {/* Left: Illustration */}
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 0.8, ease: "easeOut" }}
              className="relative h-[500px] lg:h-[600px] rounded-[32px] overflow-hidden shadow-premium"
            >
              <img 
                src="https://images.unsplash.com/photo-1581578731548-c64695cc6952?w=800&q=80" 
                alt="Why Choose Us" 
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-brand-primary/10 mix-blend-multiply"></div>
            </motion.div>

            {/* Right: Content & Checklist */}
            <motion.div
              variants={containerVariants}
              initial="hidden"
              whileInView="show"
              viewport={{ once: true, margin: "-100px" }}
              className="space-y-10"
            >
              <div className="space-y-4">
                <h2 className="text-4xl md:text-5xl font-bold text-brand-text tracking-tight">Why Choose Us</h2>
                <p className="text-lg md:text-xl text-brand-muted max-w-lg leading-relaxed">
                  Experience the most reliable, secure, and seamless home services platform built for modern living.
                </p>
              </div>

              <div className="grid sm:grid-cols-2 gap-6">
                {features.map((feature, idx) => (
                  <motion.div key={idx} variants={itemVariants} className="flex items-center gap-4 group">
                    <div className="w-12 h-12 rounded-[14px] bg-brand-primary/10 flex items-center justify-center text-brand-primary group-hover:bg-brand-primary group-hover:text-white transition-colors duration-300">
                      <CheckCircle2 className="w-6 h-6" />
                    </div>
                    <span className="text-lg font-semibold text-brand-text">{feature}</span>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Animated Statistics (Blue Gradient Section) */}
      <section className="py-20 bg-gradient-to-r from-blue-600 to-indigo-600 text-white relative overflow-hidden shadow-inner">
        <div className="absolute inset-0 opacity-30 bg-[radial-gradient(circle_at_center,_#EEF5FF_0%,_transparent_70%)]"></div>
        <div className="container mx-auto px-4 md:px-6 relative z-10">
          <motion.div 
            variants={containerVariants}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true }}
            className="grid grid-cols-2 md:grid-cols-4 gap-12 md:gap-8 divide-x divide-white/10"
          >
            {stats.map((stat, idx) => (
              <motion.div key={idx} variants={itemVariants} className="flex flex-col items-center justify-center text-center px-4">
                <span className="text-4xl md:text-5xl font-bold mb-3 text-brand-accent tracking-tighter">{stat.value}</span>
                <span className="text-sm md:text-base font-semibold text-white/70 uppercase tracking-widest">{stat.label}</span>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>
    </div>
  );
}
